import { useState, useEffect, lazy } from 'react'
import { useAppSelector, useAppDispatch, useAppThunkDispatch } from '@utils/hooks/reduxHooks'
import { Route, Routes, Navigate, Outlet } from 'react-router-dom'
import LogRocket from 'logrocket'
import { injectGlobal } from '@emotion/css'

import { checkAuthenticationStatus } from './redux/slices/auth'
import PrivateRoute from './router/PrivateRoute'
import withClearCache from './ClearCache'
import { getBuildDate } from './utils/dateUtil'
import packageJson from '../package.json'

import MainLayout from './components/Layout/MainLayout'
import Spinner from './components/ui/Spinner'

injectGlobal`
    .react-contextmenu {
      background-color: #fff;
      background-clip: padding-box;
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 0.25rem;
      color: #373a3c;
      font-size: 16px;
      margin: 2px 0 0;
      min-width: 160px;
      outline: none;
      opacity: 0;
      padding: 5px 0;
      pointer-events: none;
      text-align: left;
      transition: opacity 250ms ease !important;
    }
    .react-contextmenu.react-contextmenu--visible {
      opacity: 1;
      pointer-events: auto;
    }
    .react-contextmenu-item {
      background: 0 0;
      border: 0;
      color: #373a3c;
      cursor: pointer;
      font-weight: 400;
      line-height: 1.5;
      padding: 3px 20px;
      text-align: inherit;
      white-space: nowrap;
    }
    .react-contextmenu-item.react-contextmenu-item--active,
    .react-contextmenu-item.react-contextmenu-item--selected {
      color: #fff;
      background-color: #20a0ff;
      border-color: #20a0ff;
      text-decoration: none;
    }
    .react-contextmenu-item.react-contextmenu-item--disabled,
    .react-contextmenu-item.react-contextmenu-item--disabled:hover {
      background-color: transparent;
      border-color: rgba(0, 0, 0, 0.15);
      color: #878a8c;
    }
    .react-contextmenu-item--divider {
      border-bottom: 1px solid rgba(0, 0, 0, 0.15);
      cursor: inherit;
      margin-bottom: 3px;
      padding: 2px 0;
    }
    .react-contextmenu-item--divider:hover {
      background-color: transparent;
      border-color: rgba(0, 0, 0, 0.15);
    }
    .react-contextmenu-item.react-contextmenu-submenu {
      padding: 0;
    }
    .react-contextmenu-item.react-contextmenu-submenu > .react-contextmenu-item::after {
      content: '▶';
      display: inline-block;
      position: absolute;
      right: 7px;
    }
    .example-multiple-targets::after {
      content: attr(data-count);
      display: block;
    }
`

const Login = lazy(() => import('@src/pages/Auth/Login'))
const SignUp = lazy(() => import('@src/pages/Auth/SignUp'))
const NewPassword = lazy(() => import('@src/pages/Auth/NewPassword'))
const ForgotPassword = lazy(() => import('@src/pages/Auth/ForgotPassword'))
const OfflineQuotes = lazy(() => import('@src/pages/OfflineQuotes'))
const Clients = lazy(() => import('@src/pages/Clients'))
const Freelancer = lazy(() => import('@src/pages/Freelancer'))
const Projects = lazy(() => import('@src/pages/Projects'))
const ProjectChildren = lazy(() => import('@src/pages/ProjectChildren'))
const QuoteGridPage1 = lazy(() => import('@src/pages/QuoteGrid/QuoteGridPage1'))
const PrepQuotePage = lazy(() => import('@src/pages/QuoteGrid/PrepQuotePage'))
const WrapRecapQuotePage = lazy(() => import('@src/pages/QuoteGrid/WrapRecapPage'))
const Contacts = lazy(() => import('@src/pages/Contacts'))
const Suppliers = lazy(() => import('@src/pages/Suppliers'))
const Proposals = lazy(() => import('@src/pages/Proposals'))
const Invoices = lazy(() => import('@src/pages/Invoices'))
const Purchases = lazy(() => import('@src/pages/Purchases'))
const Dashboard = lazy(() => import('@src/pages/Dashboard'))

const LOGROCKET_ACCESS_ID = process.env.LOGROCKET_ACCESS_ID || 'jfdi11/chedar'

// * HOC for clearing cache if build is different
const ClearCacheComponent = withClearCache(MainApp)

function App() {
  return <ClearCacheComponent />
}

function MainApp() {
  LogRocket.init(LOGROCKET_ACCESS_ID, {
    release: JSON.stringify(packageJson.buildDate),
    network: {
      requestSanitizer: request => {
        if (request.url.includes('cognito')) {
          // ignore the request response pair
          return null
        }
        // otherwise log the request normally
        return request
      },
    },
  })

  console.log('getBuildDate: ', getBuildDate(packageJson.buildDate))

  const dispatch = useAppDispatch()
  const dispatchThunk = useAppThunkDispatch()
  const userLoginStatus = useAppSelector(state => state.auth.userLoginStatus)
  const initialLoginApiStatus = useAppSelector(state => state.auth.initialLoginApiStatus)
  const confirmApiStatus = useAppSelector(state => state.auth.confirmApiStatus)
  // Todo Remove as any assertion after auth slice has been typed
  const userInfo = useAppSelector(state => state.auth.userInfo) as any
  const userGroups = userInfo.groups
  const [userCheck, setUserCheck] = useState(false)

  // Checks is user is authenticated as soon as app is loaded; works in offline mode based on cognito token cookies
  useEffect(() => {
    dispatchThunk(checkAuthenticationStatus())
  }, [dispatchThunk])

  useEffect(() => {
    if (
      initialLoginApiStatus === 'idle' ||
      initialLoginApiStatus === 'loading' ||
      !initialLoginApiStatus
    ) {
      setUserCheck(false)
    } else {
      setUserCheck(true)
    }
  }, [initialLoginApiStatus])

  return (
    <>
      {/* Routes are rendered after the initial check for authenticated user is done */}
      {!userCheck ? (
        <Spinner />
      ) : (
        <Routes>
          <Route path='/' element={<MainLayout />}>
            <Route path='/login' element={<Login />} />
            <Route path='/signup' element={<SignUp />} />
            <Route path='/newPassword' element={<NewPassword />} />
            <Route path='/forgotPassword' element={<ForgotPassword />} />
            <Route path='/*' element={<Navigate to='/' />} />
            {/* All the below routes can only be accessed by authenticated users */}
            {confirmApiStatus === 'failed' && <Navigate to='/signup' />}
            <PrivateRoute isAuth={userLoginStatus} path='/' component={Outlet}>
              {/* If logged in user belongs to freelancer group in cognito they are shown /freelancer as the homepage */}
              <Route path='/' element={<Navigate to='/offline' replace={true} />} />
              <Route path='/quote/v1/:quoteId/budget' element={<QuoteGridPage1 />} />
              <Route path='/quote/v1/:quoteId/prep' element={<PrepQuotePage />} />
              <Route path='/quote/v1/:quoteId/wrap' element={<WrapRecapQuotePage />} />
              <Route path='/offline' element={<OfflineQuotes />} />
            </PrivateRoute>
          </Route>
        </Routes>
      )}
    </>
  )
}

export default App
